/**
 * Reloads the game deliberately and says so afterwards (M30 #21: "the game keeps resetting back to the launch screen
 * when I finish some of the levels"). A reload the game makes itself — onto a newer build, or after the GPU took the
 * game's WebGL context away for good — used to land the player on the launch screen with no word of why, as if the
 * game had crashed. Now the reason is left in sessionStorage first; the page that loads reads it, skips TAP TO START,
 * goes straight back to the hub and says what happened.
 *
 * sessionStorage survives a reload of the same tab (and of a Home Screen app's page) but not a fresh launch, and a
 * note older than MaxAgeMs is ignored, so an ordinary later start never mistakes itself for a reload.
 */
export type ReloadReason = "update" | "graphics";

export interface ReloadNote { readonly reason: ReloadReason; readonly at: number }

/** Where the note is kept; injectable for tests. */
export interface NoteStore { getItem(k: string): string | null; setItem(k: string, v: string): void; removeItem(k: string): void }

export const ReloadNoteKey = "highmarch.reloadNote";
/** A note this old is not this page's reload (ms). */
export const ReloadNoteMaxAgeMs = 120_000;

function defaultStore(): NoteStore | null {
  try { return typeof sessionStorage !== "undefined" ? sessionStorage : null; } catch { return null; }
}

/** Leaves the note, then reloads. */
export function reloadFor(reason: ReloadReason, reload: () => void = () => location.reload(), store: NoteStore | null = defaultStore(), now = Date.now()): void {
  try { store?.setItem(ReloadNoteKey, JSON.stringify({ reason, at: now })); } catch { /* private mode: reload anyway */ }
  reload();
}

/** The note the last reload left, if it is this page's (read once: it is removed). */
export function takeReloadNote(store: NoteStore | null = defaultStore(), now = Date.now()): ReloadNote | null {
  if (!store) return null;
  let raw: string | null = null;
  try { raw = store.getItem(ReloadNoteKey); store.removeItem(ReloadNoteKey); } catch { return null; }
  if (!raw) return null;
  try {
    const n = JSON.parse(raw) as Partial<ReloadNote>;
    if ((n.reason !== "update" && n.reason !== "graphics") || typeof n.at !== "number") return null;
    return now - n.at <= ReloadNoteMaxAgeMs && now >= n.at ? { reason: n.reason, at: n.at } : null;
  } catch { return null; }
}

/** What the hub says after the reload. */
export function reloadNoteText(n: ReloadNote): string {
  return n.reason === "update" ? "Updated to the latest version" : "The scene was redrawn after a hiccup. Your progress is safe";
}
